import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import moment from 'moment';
import { deleteKbArticle, archiveKbArticle } from './kbActions';
import { updateKbArticleStatus, updateKbArticleVisibility } from './kbBulkActions';

export const getKbArticles = createAsyncThunk(
    'kbArticles/getKbArticles',
    async ({ business_id, group_id, page, limit, status, visibility, search }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) {
                throw new Error('Business ID not found');
            }
            if (!group_id) {
                throw new Error('Group ID not found');
            }

            let url = `${ROOT_URL}/kb/articles`;
            const params = new URLSearchParams();

            params.append('business_id', business_id);
            params.append('group_id', group_id);
            params.append('page', page);
            params.append('limit', limit);

            if (status && status !== 'all') {
                params.append('status', status);
            }

            if (visibility && visibility !== 'All') {
                params.append('visibility', visibility.toLowerCase());
            }

            if (search) {
                params.append('search', search);
            }

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching KB articles:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch articles'
            );
        }
    }
);

/** Per-tab totals honouring the active search, since /kb/articles/stats ignores `search`. */
export const getKbArticleSearchCounts = createAsyncThunk(
    'kbArticles/getKbArticleSearchCounts',
    async ({ business_id, group_id, visibility, search }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) throw new Error('Business ID not found');
            if (!group_id) throw new Error('Group ID not found');

            const fetchTotal = async (status) => {
                const params = new URLSearchParams({
                    business_id,
                    group_id,
                    page: '1',
                    limit: '1',
                });
                if (status !== 'all') params.append('status', status);
                if (visibility && visibility !== 'All') {
                    params.append('visibility', visibility.toLowerCase());
                }
                if (search) params.append('search', search);

                const response = await axios.get(`${ROOT_URL}/kb/articles?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                });
                return response.data?.data?.pagination?.total || 0;
            };

            const [all, published, draft, pendingReview, archived] = await Promise.all([
                fetchTotal('all'),
                fetchTotal('published'),
                fetchTotal('draft'),
                fetchTotal('pendingReview'),
                fetchTotal('archived'),
            ]);

            return { all, published, draft, pendingReview, archived };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch article counts'
            );
        }
    }
);

const emptySearchCounts = {
    all: 0,
    published: 0,
    draft: 0,
    pendingReview: 0,
    archived: 0,
};

const initialState = {
    loading: true,
    error: null,
    articles: [],
    totalCount: 0,
    searchCounts: { ...emptySearchCounts },
    totalPages: 0,
    currentPage: 0,
    lastFetchedGroupId: null,
    activeRequestId: null,
};

const kbArticlesSlice = createSlice({
    name: 'kbArticles',
    initialState,
    reducers: {
        resetKbArticles: (state) => {
            return initialState;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getKbArticles.pending, (state, action) => {
                state.activeRequestId = action.meta.requestId;
                const groupId = action.meta?.arg?.group_id ?? null;
                const sameGroup =
                    state.lastFetchedGroupId != null &&
                    String(state.lastFetchedGroupId) === String(groupId);
                const hasData = !!state.articles?.length || !!state.totalCount;
                // Keep prior rows under skeleton until the new response arrives (no empty blink).
                state.loading = !sameGroup || !hasData;
                state.error = null;
            })
            .addCase(getKbArticles.fulfilled, (state, action) => {
                if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
                    return;
                }
                state.loading = false;
                state.error = null;
                state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;

                const data = action?.payload.data || {};
                const articlesList = data?.articles || [];
                const pagination = data?.pagination || {};

                state.articles = articlesList.map(article => ({
                    ...article,
                    id: article?._id,
                    createdBy: article?.authorId?.full_name || 'Unknown',
                    category: article?.categoryIds?.[0]?.name || '-',
                    updated: article?.updatedAt ? moment(article?.updatedAt).fromNow() : 'Unknown',
                    status: article?.status ? article?.status?.charAt(0).toUpperCase() + article.status.slice(1).toLowerCase() : '',
                    visibility: article?.visibility ? article?.visibility?.charAt(0).toUpperCase() + article?.visibility.slice(1).toLowerCase() : ''
                }));

                state.totalCount = pagination.total || 0;
                state.totalPages = pagination.totalPages || 0;
                state.currentPage = pagination.page || 0;
            })
            .addCase(getKbArticles.rejected, (state, action) => {
                if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
                    return;
                }
                state.loading = false;
                state.error = action.payload;
                state.articles = [];
                state.totalCount = 0;
            })
            // Search-aware tab counts (previous values persist while refetching, so tabs don't blink to 0)
            .addCase(getKbArticleSearchCounts.fulfilled, (state, action) => {
                state.searchCounts = { ...emptySearchCounts, ...action.payload };
            })
            .addCase(getKbArticleSearchCounts.rejected, (state) => {
                state.searchCounts = { ...emptySearchCounts };
            })
            // Optimistic Updates

            .addCase(archiveKbArticle.pending, (state, action) => {
                const idToArchive = action.meta.arg.articleId;
                const article = state.articles.find(a => a.id === idToArchive);
                if (article) {
                    article.status = 'Archived';
                }
            })
            // Bulk Optimistic Updates
            .addCase(updateKbArticleStatus.pending, (state, action) => {
                const { articleIds, status } = action.meta.arg;
                const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1).replace(/([A-Z])/g, ' $1').trim();
                // Special handling for camelCase pendingReview -> Pending Review if needed, or rely on simple formatting if passed clean
                // status passed from BulkBar is 'published', 'draft', 'pendingReview', 'archived'.
                // 'pendingReview' -> 'Pendingreview' via simple logic? no.
                let displayStatus = status.charAt(0).toUpperCase() + status.slice(1);
                if (status === 'pendingReview') displayStatus = 'Pending Review';
                if (status === 'draft') displayStatus = 'Draft'; // or Drafts? Table usually shows 'Draft'.

                state.articles.forEach(article => {
                    if (articleIds.includes(article.id)) {
                        article.status = displayStatus;
                    }
                });
            })
            .addCase(updateKbArticleVisibility.pending, (state, action) => {
                const { articleIds, visibility } = action.meta.arg;
                const displayVisibility = visibility.charAt(0).toUpperCase() + visibility.slice(1).toLowerCase();

                state.articles.forEach(article => {
                    if (articleIds.includes(article.id)) {
                        article.visibility = displayVisibility;
                    }
                });
            })
            // Feature Article Optimistic Update
            .addCase(toggleArticleFeature.pending, (state, action) => {
                const { articleId, isFeatured } = action.meta.arg;
                const article = state.articles.find(a => a.id === articleId);
                if (article) {
                    article.isFeatured = isFeatured;
                }
            });
    }
});

export const toggleArticleFeature = createAsyncThunk(
    'kbArticles/toggleArticleFeature',
    async ({ articleId, isFeatured, group_id }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            const url = `${ROOT_URL}/kb/articles/feature/${articleId}`;

            const response = await axios.patch(url, { isFeatured, group_id }, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update feature status'
            );
        }
    }
);

export const { resetKbArticles, clearError } = kbArticlesSlice.actions;

export default kbArticlesSlice.reducer;
